import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  CheckCircle2,
  Eye,
  EyeOff,
  Film,
  GripVertical,
  Link2,
  Loader2,
  Pencil,
  Plus,
  Trash2,
  Upload,
  X,
  Youtube,
} from "lucide-react";
import { toast } from "sonner";
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import AppLayout from "@/components/AppLayout";
import BilingualText from "@/components/BilingualText";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";

const emptyLesson = () => ({
  slug: "",
  level: "A2",
  title_cn: "",
  title_en: "",
  description_cn: "",
  description_en: "",
  grade_hint: "",
  duration_est: 240,
  video_url: "",
  video_source: "",
  status: "draft",
});

const slugify = (s) =>
  (s || "")
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-")
    .slice(0, 60);

function detectSource(url) {
  const u = (url || "").toLowerCase();
  if (u.includes("youtube.com") || u.includes("youtu.be")) return "youtube";
  if (u.includes("heygen.com")) return "heygen";
  if (u) return "direct";
  return "";
}

export default function AdminLessons() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [isAdmin, setIsAdmin] = useState(null);
  const [lessons, setLessons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  // Form state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null); // null = create
  const [form, setForm] = useState(emptyLesson());
  const [saving, setSaving] = useState(false);
  const [urlInput, setUrlInput] = useState({}); // { [id]: string }

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/lessons");
      setLessons(data || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!user) {
      nav("/auth?mode=login");
      return;
    }
    api
      .get("/admin/me")
      .then(({ data }) => {
        setIsAdmin(!!data.is_admin);
        if (data.is_admin) load();
      })
      .catch(() => setIsAdmin(false));
  }, [user, nav]);

  // ---- CRUD handlers ----
  const openCreate = () => {
    setEditingId(null);
    setForm(emptyLesson());
    setDialogOpen(true);
  };

  const openEdit = (lesson) => {
    setEditingId(lesson.id);
    setForm({
      slug: lesson.slug || "",
      level: lesson.level || "A2",
      title_cn: lesson.title_cn || "",
      title_en: lesson.title_en || "",
      description_cn: lesson.description_cn || "",
      description_en: lesson.description_en || "",
      grade_hint: lesson.grade_hint || "",
      duration_est: lesson.duration_est || 240,
      video_url: lesson.video_url || "",
      video_source: lesson.video_source || "",
      status: lesson.status || "draft",
    });
    setDialogOpen(true);
  };

  const submitForm = async () => {
    if (!form.title_en.trim() || !form.title_cn.trim()) {
      toast.error("Both English and Chinese titles are required");
      return;
    }
    if (!form.slug.trim()) {
      toast.error("Slug is required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        slug: form.slug.trim(),
        level: form.level,
        title_cn: form.title_cn.trim(),
        title_en: form.title_en.trim(),
        description_cn: form.description_cn,
        description_en: form.description_en,
        grade_hint: form.grade_hint,
        duration_est: Number(form.duration_est) || 240,
        status: form.status,
      };
      if (form.video_url?.trim()) {
        payload.video_url = form.video_url.trim();
        payload.video_source = detectSource(form.video_url);
      }
      if (editingId) {
        await api.patch(`/admin/lessons/${editingId}`, payload);
        // If video URL changed, also PATCH via video-url endpoint so source syncs
        if (payload.video_url) {
          await api.post(`/admin/lessons/${editingId}/video-url`, {
            url: payload.video_url,
          });
        }
        toast.success("Lesson updated");
      } else {
        await api.post("/admin/lessons", payload);
        toast.success("Lesson created");
      }
      setDialogOpen(false);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (lesson) => {
    if (lesson.seed) {
      toast.error("Seed lessons cannot be deleted. Set to Draft to hide.");
      return;
    }
    if (!window.confirm(`Delete lesson "${lesson.title_en}"? This cannot be undone.`)) return;
    setBusyId(lesson.id);
    try {
      await api.delete(`/admin/lessons/${lesson.id}`);
      toast.success("Lesson deleted");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    } finally {
      setBusyId(null);
    }
  };

  const togglePublish = async (lesson) => {
    setBusyId(lesson.id);
    try {
      const next = lesson.status === "published" ? "draft" : "published";
      await api.patch(`/admin/lessons/${lesson.id}`, { status: next });
      toast.success(next === "published" ? "Published" : "Set to draft");
      await load();
    } catch {
      toast.error("Toggle failed");
    } finally {
      setBusyId(null);
    }
  };

  const uploadFile = async (lesson, file) => {
    if (!file) return;
    if (file.size > 200 * 1024 * 1024) {
      toast.error("File > 200MB. Please compress before uploading.");
      return;
    }
    setBusyId(lesson.id);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/admin/lessons/${lesson.id}/video`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Uploaded ${file.name}`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setBusyId(null);
    }
  };

  const saveUrl = async (lesson) => {
    const url = (urlInput[lesson.id] || "").trim();
    if (!url) {
      toast.error("Paste a video URL first");
      return;
    }
    setBusyId(lesson.id);
    try {
      await api.post(`/admin/lessons/${lesson.id}/video-url`, { url });
      toast.success("Video URL saved");
      setUrlInput((s) => ({ ...s, [lesson.id]: "" }));
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setBusyId(null);
    }
  };

  const removeVideo = async (lesson) => {
    setBusyId(lesson.id);
    try {
      await api.delete(`/admin/lessons/${lesson.id}/video`);
      toast.success("Video removed");
      await load();
    } catch {
      toast.error("Remove failed");
    } finally {
      setBusyId(null);
    }
  };

  // ---- Drag-and-drop reorder ----
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const onDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = lessons.findIndex((l) => l.id === active.id);
    const newIdx = lessons.findIndex((l) => l.id === over.id);
    if (oldIdx < 0 || newIdx < 0) return;
    const next = arrayMove(lessons, oldIdx, newIdx).map((l, i) => ({
      ...l,
      sort_order: i,
    }));
    setLessons(next);
    try {
      await api.post("/admin/lessons/reorder", {
        order: next.map((l, i) => ({ id: l.id, sort_order: i })),
      });
    } catch {
      toast.error("Reorder failed — refresh to sync");
    }
  };

  if (isAdmin === null) {
    return (
      <AppLayout>
        <div className="grid place-items-center py-24">
          <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
        </div>
      </AppLayout>
    );
  }

  if (!isAdmin) {
    return (
      <AppLayout>
        <div className="card-plush p-10 max-w-2xl mx-auto text-center space-y-4">
          <BilingualText cn="仅限管理员" en="Admin only" size="lg" className="items-center" />
          <p className="text-sm text-slate-600 leading-relaxed">
            <span className="cn block mb-1">
              请将您的账户邮箱加入 <code className="bg-slate-100 rounded px-1">ADMIN_EMAILS</code>
              环境变量中（后端 .env）。
            </span>
            Add your account email to the <code className="bg-slate-100 rounded px-1">ADMIN_EMAILS</code> env
            variable in <code className="bg-slate-100 rounded px-1">backend/.env</code> (comma-separated), then restart the backend.
          </p>
          <Button asChild variant="outline" className="rounded-full">
            <Link to="/dashboard">Back to dashboard</Link>
          </Button>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div data-testid="admin-lessons-root" className="max-w-5xl mx-auto space-y-8">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-4">
            <span className="w-12 h-12 rounded-2xl bg-gradient-to-br from-amber-100 to-emerald-100 grid place-items-center">
              <Film className="w-5 h-5 text-emerald-700" />
            </span>
            <div>
              <BilingualText cn="课程管理" en="Lesson manager" size="hero" />
              <p className="mt-1 text-sm text-slate-500">
                <span className="cn mr-1">添加、编辑或重新排列你的课程。</span>
                Add, edit, or rearrange your bilingual lessons.
              </p>
            </div>
          </div>
          <Button
            className="rounded-full bg-slate-900 hover:bg-slate-800 text-white font-bold shadow-md h-11"
            onClick={openCreate}
            data-testid="admin-add-lesson-btn"
          >
            <Plus className="w-4 h-4 mr-1.5" />
            <span className="cn mr-1">新增课程</span>New lesson
          </Button>
        </div>

        {/* Legend */}
        <div className="text-xs text-slate-500 border border-slate-100 rounded-2xl p-4 bg-white/60 leading-relaxed">
          <p>
            <span className="cn block text-slate-700 mb-1">
              上传本地视频（MP4/WebM，最大 200MB），或粘贴直链、YouTube、HeyGen 分享链接。
            </span>
            Upload a local file (MP4/WebM, up to 200MB) <b>or</b> paste an external URL — direct MP4, YouTube, or HeyGen share links are all supported.
          </p>
        </div>

        {loading ? (
          <div className="grid place-items-center py-16">
            <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
          </div>
        ) : (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
            <SortableContext
              items={lessons.map((l) => l.id)}
              strategy={verticalListSortingStrategy}
            >
              <div className="space-y-4">
                {lessons.map((l) => (
                  <SortableLessonRow
                    key={l.id}
                    lesson={l}
                    busy={busyId === l.id}
                    urlInput={urlInput[l.id] || ""}
                    setUrlInput={(v) => setUrlInput((s) => ({ ...s, [l.id]: v }))}
                    onEdit={() => openEdit(l)}
                    onDelete={() => remove(l)}
                    onTogglePublish={() => togglePublish(l)}
                    onUploadFile={(f) => uploadFile(l, f)}
                    onSaveUrl={() => saveUrl(l)}
                    onRemoveVideo={() => removeVideo(l)}
                  />
                ))}
                {!lessons.length && (
                  <div className="card-plush p-10 text-center text-slate-400">
                    No lessons yet. Click <b>New lesson</b> to create your first one.
                  </div>
                )}
              </div>
            </SortableContext>
          </DndContext>
        )}
      </div>

      {/* Create / edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="admin-lesson-dialog">
          <DialogHeader>
            <DialogTitle>
              {editingId ? "Edit lesson" : "New lesson"}
            </DialogTitle>
            <DialogDescription>
              Bilingual lesson · Chinese title above English. Paste a YouTube / HeyGen /
              direct MP4 URL, or save the lesson and upload a file from its row.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label htmlFor="title_cn" className="text-xs font-bold text-slate-600">Chinese title · 中文标题</Label>
                <Input
                  id="title_cn"
                  value={form.title_cn}
                  onChange={(e) => {
                    const v = e.target.value;
                    setForm((f) => ({
                      ...f,
                      title_cn: v,
                    }));
                  }}
                  placeholder="例如：主语 · 动词 · 宾语"
                  className="mt-1 cn"
                  data-testid="admin-form-title-cn"
                />
              </div>
              <div>
                <Label htmlFor="title_en" className="text-xs font-bold text-slate-600">English title</Label>
                <Input
                  id="title_en"
                  value={form.title_en}
                  onChange={(e) => {
                    const v = e.target.value;
                    setForm((f) => ({
                      ...f,
                      title_en: v,
                      // Auto-slug if slug empty AND creating (not editing)
                      slug: !editingId && !f.slug ? slugify(v) : f.slug,
                    }));
                  }}
                  placeholder="e.g. Subject · Verb · Object"
                  className="mt-1"
                  data-testid="admin-form-title-en"
                />
              </div>
            </div>

            <div className="grid sm:grid-cols-3 gap-3">
              <div className="sm:col-span-2">
                <Label htmlFor="slug" className="text-xs font-bold text-slate-600">Slug (URL id)</Label>
                <Input
                  id="slug"
                  value={form.slug}
                  onChange={(e) => setForm((f) => ({ ...f, slug: slugify(e.target.value) }))}
                  placeholder="e.g. svo-grade-1"
                  className="mt-1 font-mono text-sm"
                  data-testid="admin-form-slug"
                />
              </div>
              <div>
                <Label className="text-xs font-bold text-slate-600">Level</Label>
                <Select
                  value={form.level}
                  onValueChange={(v) => setForm((f) => ({ ...f, level: v }))}
                >
                  <SelectTrigger className="mt-1" data-testid="admin-form-level">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="A2">A2 · Key (7–9)</SelectItem>
                    <SelectItem value="B1">B1 · Preliminary (10–12)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label htmlFor="desc_cn" className="text-xs font-bold text-slate-600">Chinese description · 简介</Label>
                <Textarea
                  id="desc_cn"
                  value={form.description_cn}
                  onChange={(e) => setForm((f) => ({ ...f, description_cn: e.target.value }))}
                  rows={3}
                  className="mt-1 cn resize-none"
                  data-testid="admin-form-desc-cn"
                />
              </div>
              <div>
                <Label htmlFor="desc_en" className="text-xs font-bold text-slate-600">English description</Label>
                <Textarea
                  id="desc_en"
                  value={form.description_en}
                  onChange={(e) => setForm((f) => ({ ...f, description_en: e.target.value }))}
                  rows={3}
                  className="mt-1 resize-none"
                  data-testid="admin-form-desc-en"
                />
              </div>
            </div>

            <div className="grid sm:grid-cols-3 gap-3">
              <div>
                <Label htmlFor="grade_hint" className="text-xs font-bold text-slate-600">Grade label</Label>
                <Input
                  id="grade_hint"
                  value={form.grade_hint}
                  onChange={(e) => setForm((f) => ({ ...f, grade_hint: e.target.value }))}
                  placeholder="e.g. Grade 1"
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="duration" className="text-xs font-bold text-slate-600">Duration (sec)</Label>
                <Input
                  id="duration"
                  type="number"
                  min={30}
                  value={form.duration_est}
                  onChange={(e) => setForm((f) => ({ ...f, duration_est: e.target.value }))}
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-xs font-bold text-slate-600">Status</Label>
                <Select
                  value={form.status}
                  onValueChange={(v) => setForm((f) => ({ ...f, status: v }))}
                >
                  <SelectTrigger className="mt-1" data-testid="admin-form-status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="published">Published</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label htmlFor="video_url" className="text-xs font-bold text-slate-600">
                Video URL (optional — paste direct MP4, YouTube, or HeyGen link)
              </Label>
              <Input
                id="video_url"
                value={form.video_url}
                onChange={(e) => setForm((f) => ({ ...f, video_url: e.target.value }))}
                placeholder="https://share.heygen.com/… or https://youtu.be/… or https://…/video.mp4"
                className="mt-1 text-sm"
                data-testid="admin-form-video-url"
              />
              {form.video_url && (
                <p className="mt-1 text-xs text-slate-500">
                  Detected source: <b className="text-slate-700">{detectSource(form.video_url) || "direct"}</b>
                </p>
              )}
              <p className="mt-2 text-xs text-slate-400 leading-relaxed">
                Prefer to upload a file? Save the lesson first, then use the <b>Upload video</b> button on its row.
              </p>
            </div>
          </div>

          <DialogFooter className="mt-4">
            <Button
              variant="outline"
              className="rounded-full"
              onClick={() => setDialogOpen(false)}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              className="rounded-full bg-slate-900 hover:bg-slate-800 text-white font-bold"
              onClick={submitForm}
              disabled={saving}
              data-testid="admin-form-submit"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : null}
              {editingId ? "Save changes" : "Create lesson"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}

function SortableLessonRow({
  lesson,
  busy,
  urlInput,
  setUrlInput,
  onEdit,
  onDelete,
  onTogglePublish,
  onUploadFile,
  onSaveUrl,
  onRemoveVideo,
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: lesson.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };
  const isPublished = lesson.status === "published";

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="card-plush p-5 sm:p-6"
      data-testid={`admin-lesson-${lesson.slug}`}
    >
      <div className="flex items-start gap-3">
        <button
          {...attributes}
          {...listeners}
          className="mt-1 shrink-0 text-slate-300 hover:text-slate-500 cursor-grab active:cursor-grabbing p-1"
          aria-label="Drag to reorder"
          data-testid={`admin-lesson-drag-${lesson.slug}`}
        >
          <GripVertical className="w-5 h-5" />
        </button>

        <div className="flex-1 min-w-0 space-y-3">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="min-w-0">
              <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                <span className="badge-soft">{lesson.level}</span>
                {lesson.grade_hint && (
                  <span className="badge-soft bg-emerald-50 text-emerald-700">
                    {lesson.grade_hint}
                  </span>
                )}
                {lesson.seed && (
                  <span className="badge-soft bg-amber-50 text-amber-700">Seed</span>
                )}
                {isPublished ? (
                  <span className="badge-soft bg-emerald-100 text-emerald-800">
                    <Eye className="w-3 h-3" />
                    Published
                  </span>
                ) : (
                  <span className="badge-soft bg-slate-100 text-slate-500">
                    <EyeOff className="w-3 h-3" />
                    Draft
                  </span>
                )}
                {lesson.has_video && (
                  <span className="badge-soft bg-rose-100 text-rose-700">
                    <CheckCircle2 className="w-3 h-3" />
                    Video: {lesson.video_source || "linked"}
                  </span>
                )}
              </div>
              <BilingualText cn={lesson.title_cn} en={lesson.title_en} size="md" />
              <code className="mt-1 block text-[10px] text-slate-400 break-all">
                /{lesson.slug}
              </code>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <label className="text-xs font-bold text-slate-500 flex items-center gap-2">
                <Switch
                  checked={isPublished}
                  onCheckedChange={onTogglePublish}
                  disabled={busy}
                  data-testid={`admin-toggle-${lesson.slug}`}
                />
                <span>Publish</span>
              </label>
              <Button
                variant="outline"
                size="sm"
                className="rounded-full h-9"
                onClick={onEdit}
                disabled={busy}
                data-testid={`admin-edit-${lesson.slug}`}
              >
                <Pencil className="w-3.5 h-3.5 mr-1" />
                Edit
              </Button>
              <Button
                asChild
                variant="ghost"
                size="sm"
                className="rounded-full h-9"
              >
                <Link to={`/lessons/${lesson.slug}`}>Preview</Link>
              </Button>
              {!lesson.seed && (
                <Button
                  variant="outline"
                  size="sm"
                  className="rounded-full h-9 text-rose-600 hover:text-rose-700 hover:bg-rose-50 border-rose-200"
                  onClick={onDelete}
                  disabled={busy}
                  data-testid={`admin-delete-${lesson.slug}`}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              )}
            </div>
          </div>

          {/* Video controls */}
          <div className="grid md:grid-cols-2 gap-3 pt-2 border-t border-slate-100">
            <div className="space-y-2">
              <div className="text-[11px] font-black uppercase tracking-widest text-slate-500 flex items-center gap-1.5">
                <Upload className="w-3 h-3" />
                Upload file
              </div>
              <label
                className={
                  "inline-flex items-center gap-2 rounded-full px-4 h-9 text-sm font-bold cursor-pointer bg-slate-900 text-white hover:bg-slate-800 shadow-sm " +
                  (busy ? "opacity-60 pointer-events-none" : "")
                }
                data-testid={`admin-upload-${lesson.slug}`}
              >
                {busy ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Upload className="w-4 h-4" />
                )}
                {lesson.has_video ? "Replace" : "Upload"} MP4
                <input
                  type="file"
                  accept="video/mp4,video/webm,video/quicktime,video/x-m4v"
                  className="hidden"
                  disabled={busy}
                  onChange={(e) => onUploadFile(e.target.files?.[0])}
                />
              </label>
            </div>

            <div className="space-y-2">
              <div className="text-[11px] font-black uppercase tracking-widest text-slate-500 flex items-center gap-1.5">
                <Link2 className="w-3 h-3" />
                Paste URL (YouTube / HeyGen / direct MP4)
              </div>
              <div className="flex items-center gap-2">
                <Input
                  value={urlInput}
                  onChange={(e) => setUrlInput(e.target.value)}
                  placeholder="https://share.heygen.com/…"
                  className="text-xs h-9"
                  disabled={busy}
                  data-testid={`admin-url-input-${lesson.slug}`}
                />
                <Button
                  size="sm"
                  className="rounded-full h-9 bg-slate-900 hover:bg-slate-800 text-white font-bold shrink-0"
                  onClick={onSaveUrl}
                  disabled={busy || !urlInput.trim()}
                  data-testid={`admin-url-save-${lesson.slug}`}
                >
                  Save
                </Button>
              </div>
            </div>
          </div>

          {lesson.has_video && (
            <div className="flex items-center justify-between gap-3 pt-2">
              <code className="text-[10px] text-slate-400 break-all min-w-0">
                {lesson.video_url}
              </code>
              <Button
                variant="ghost"
                size="sm"
                className="rounded-full h-8 text-rose-600 hover:text-rose-700 hover:bg-rose-50 shrink-0"
                onClick={onRemoveVideo}
                disabled={busy}
                data-testid={`admin-remove-video-${lesson.slug}`}
              >
                <X className="w-3.5 h-3.5 mr-1" />
                Remove video
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
